import type { Ref } from 'react';
import { Box, MenuItem, Pagination as MuiPagination, TextField, Typography } from '@mui/material';

export interface PaginationProps {
  /** Total row count across all pages (from the page envelope's `total`). */
  total: number;
  /** Page size (the envelope's `take`). */
  take: number;
  /** Zero-based offset of the current page (the envelope's `skip`). */
  skip: number;
  /** Fired with the new `skip` offset when the page changes. */
  onPageChange: (skip: number) => void;
  /** Fired with the new `take` when the page size changes. Omit to hide the selector. */
  onPageSizeChange?: (take: number) => void;
  /** Choices for the rows-per-page selector. */
  pageSizeOptions?: number[];
  /** Label for the rows-per-page selector (translated copy). */
  rowsPerPageLabel?: string;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Paging control for {@link PagedTable} (also usable standalone). Shows a
 * language-neutral range readout (`from–to / total`), MUI page buttons, and an
 * optional rows-per-page selector. Paging is expressed in `skip`/`take` to
 * match the API's page envelope.
 */
export function Pagination({
  total,
  take,
  skip,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  rowsPerPageLabel,
  ref,
}: PaginationProps) {
  const safeTake = take > 0 ? take : 1;
  const pageCount = Math.max(1, Math.ceil(total / safeTake));
  const page = Math.min(pageCount, Math.floor(skip / safeTake) + 1);
  const from = total === 0 ? 0 : skip + 1;
  const to = Math.min(total, skip + safeTake);
  const options = pageSizeOptions.includes(take)
    ? pageSizeOptions
    : [...pageSizeOptions, take].sort((a, b) => a - b);

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        flexWrap: 'wrap',
        p: '10px 16px',
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography component="div" sx={{ fontSize: 12, color: 'text.secondary' }}>
        <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
          {from}&ndash;{to}
        </Box>{' '}
        / {total}
      </Typography>

      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {onPageSizeChange && (
          <TextField
            select
            size="small"
            label={rowsPerPageLabel}
            value={take}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            sx={{ minWidth: 88 }}
          >
            {options.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>
        )}
        <MuiPagination
          count={pageCount}
          page={page}
          onChange={(_, next) => onPageChange((next - 1) * safeTake)}
          shape="rounded"
          color="primary"
          siblingCount={1}
          boundaryCount={1}
        />
      </Box>
    </Box>
  );
}
